// Theme switcher : dark (défaut) ↔ light. Persiste dans localStorage.
// On bascule via la classe `light` sur <html> ; le dark mode est l'absence
// de cette classe (cohérent avec :root par défaut dans index.css).

import { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

const KEY = 'mh_theme';

// Mini event bus pour synchroniser tous les useTheme() actifs : sans ça,
// chaque appel a son state local et le toggle ne se propage qu'au composant
// qui l'a déclenché jusqu'au prochain refresh.
const listeners = new Set();
const notify = (t) => listeners.forEach((fn) => fn(t));

function readTheme() {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' ? v : 'dark';
}

function applyTheme(theme) {
  const html = document.documentElement;
  // `light` pilote nos CSS variables custom (cf. index.css).
  // `dark` permet d'utiliser les variants `dark:` de Tailwind.
  if (theme === 'light') {
    html.classList.add('light');
    html.classList.remove('dark');
  } else {
    html.classList.add('dark');
    html.classList.remove('light');
  }
}

export function useTheme() {
  const [theme, setLocal] = useState(readTheme);

  useEffect(() => {
    listeners.add(setLocal);
    return () => listeners.delete(setLocal);
  }, []);

  const setTheme = (t) => {
    localStorage.setItem(KEY, t);
    applyTheme(t);
    notify(t);
  };

  return [theme, setTheme];
}

// Appel direct au tout premier render (avant React) pour éviter le flash blanc.
export function bootstrapTheme() {
  applyTheme(readTheme());
}

// Petit composant bouton de bascule, utilisable n'importe où dans l'UI.
export function ThemeToggle({ className = '' }) {
  const [theme, setTheme] = useTheme();
  const isLight = theme === 'light';
  return (
    <button
      onClick={() => setTheme(isLight ? 'dark' : 'light')}
      title={isLight ? 'Passer en mode sombre' : 'Passer en mode clair'}
      className={`bg-transparent border-none text-muted-2 hover:text-fg-2 cursor-pointer ${className}`}
      aria-label="Basculer le thème"
    >
      {isLight ? <Moon size={16} strokeWidth={1.75} /> : <Sun size={16} strokeWidth={1.75} />}
    </button>
  );
}
